import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next, useTranslation } from "react-i18next";
import Backend from "i18next-http-backend";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
// TODO: разобраться можно ли day js тоже грузить динамически
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fa";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import "dayjs/locale/pt-br";
import "dayjs/locale/ru";
import "dayjs/locale/tr";
import "dayjs/locale/zh";
import "dayjs/locale/hi";
import "dayjs/locale/uk";
import "dayjs/locale/pl";

export const SUPPORTED_LANGUAGE_CODES = [
	"en",
	"de",
	"es",
	"fa",
	"fr",
	"it",
	"ja",
	"ko",
	"pt-br",
	"ru",
	"tr",
	"zh",
	"uk",
	"pl"
];

const languageDetector = new LanguageDetector();
languageDetector.init({
	order: ["querystring", "cookie", "localStorage", "sessionStorage", "navigator", "htmlTag", "path", "subdomain"]
});

export const getDetectedLanguage = (): string => {
	const detectedLangs = languageDetector.detect();
	const detectedLang = detectedLangs && detectedLangs.length > 0 ? detectedLangs[0] : "en";
	return SUPPORTED_LANGUAGE_CODES.includes(detectedLang) ? detectedLang : "en";
};

i18n.use(languageDetector)
	.use(Backend)
	.use(initReactI18next)
	.init({
		fallbackLng: "en",
		debug: false, // process.env.NODE_ENV === "development",
		load: "languageOnly",
		interpolation: {
			escapeValue: false
		},
		react: {
			transKeepBasicHtmlNodesFor: ["br", "strong", "b", "i", "p"]
		},
		backend: {
			loadPath: "/fxlauncher/webui/locales/{{lng}}/translation.json"
		}
	});

// Dynamic dayjs locale loading
// export const loadDayjsLocale = async (locale: string) => {
// 	try {
// 		await import(`dayjs/locale/${locale}`);
// 	} catch (error) {
// 		console.error(`Failed to load dayjs locale for ${locale}:`, error);
// 	}
// };

// Function to change language
export const changeLanguage = async (lang: string) => {
	if (SUPPORTED_LANGUAGE_CODES.includes(lang)) {
		await i18n.changeLanguage(lang);
		dayjs.locale(lang);
		// Set initial dayjs locale
		//await loadDayjsLocale(lang);
	} else {
		console.warn(`Unsupported language: ${lang}`);
	}
};

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.locale(getDetectedLanguage());
export const useTimeLeft = () => {
	const { t } = useTranslation();

	const timeLeft = (input: number | undefined | Date | string, referenceDate: Date | string = new Date()): string => {
		if (input === undefined) return "";

		const reference = dayjs(referenceDate);
		let targetTime: dayjs.Dayjs;

		if (typeof input === "number") {
			if (input === 0) return t("now", "Now");
			targetTime = reference.add(input, "second");
		} else {
			targetTime = dayjs(input);
		}

		const diffInSeconds = targetTime.diff(reference, "second");

		if (diffInSeconds === 0) return t("now");

		const absoluteDiff = Math.abs(diffInSeconds);
		const timeString = dayjs.duration(absoluteDiff, "seconds").humanize();

		return diffInSeconds > 0 ? timeString : t("common.TimeAgo", "{{time}} ago", { time: timeString });
	};

	return timeLeft;
};

export const useTimeLeftDetailed = () => {
	const { t } = useTranslation();

	const formatTimeLeftDetailed = (
		input: number | undefined | Date | string,
		referenceDate: Date | string = new Date()
	): string => {
		if (input === undefined) return "";

		const reference = dayjs(referenceDate);
		let targetTime: dayjs.Dayjs;

		if (typeof input === "number") {
			if (input === 0) return t("now", "Now");
			targetTime = reference.add(input, "second");
		} else {
			targetTime = dayjs(input);
		}

		const diffInSeconds = targetTime.diff(reference, "second");

		if (diffInSeconds === 0) return t("now", "Now");

		// For past times, we'll still show the detailed format but indicate it's in the past
		const isPast = diffInSeconds < 0;
		const absoluteDiff = Math.abs(diffInSeconds);

		const days = Math.floor(absoluteDiff / 86400);
		const hours = Math.floor((absoluteDiff % 86400) / 3600);
		const minutes = Math.floor((absoluteDiff % 3600) / 60);
		const seconds = Math.floor(absoluteDiff % 60);

		let result = "";

		if (days > 0) {
			result += `${days}d `;
		}

		// Always show hours, minutes, and seconds in HH:MM:SS format
		result += `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

		return isPast ? t("common.TimeAgoDetailed", "{{time}} ago", { time: result }) : result;
	};

	return formatTimeLeftDetailed;
};
export default i18n;
